import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, Search, X, List, CalendarDays } from "lucide-react";
import toast from "react-hot-toast";
import { format, parseISO, isValid } from "date-fns";
import { exportDraftCalendarToExcel } from "./utils/exportDraftCalendar";
import { useMsal } from "@azure/msal-react";
import { trackPageView, trackUserAction } from "@/lib/analytics";
import "./DraftCalendarPage.css";
import { Statuses } from "@/utils/statuses";
import { BULK_REQUEST_TO_TABLE_COL } from "@/utils/draftCalendarFields";
import type { DcTableColKey } from "@/utils/draftCalendarFields";
import { env } from "@/config/env";
import {
  useFiscalYears,
  useDraftCalendar,
  useDraftBusinesses,
  useDraftMeetingTypes,
  useAttendeeLevels,
  useLocations,
  useUpdateShowInGRC,
  useBulkUpdateStatus,
  useBulkUpdateStates,
  useBulkDraftPushToSummit,
  useTrackUserViewedComments,
  useSetDraftRead,
  useCheckDraftBusiness,
  useCloseDraftCalendar,
  useCalendarSubmissionStatus,
  useBulkUpdateDraftFields,
  useBulkAddComment,
  useDUDeclinedReasons,
  draftCalendarKeys
} from "@/services/draftCalendar";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import { usePermissions, useCanAddMeeting, useCanEditMeeting, useCanPerformBulkAction, useCanViewToggleSettings } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useQueryClient } from "@tanstack/react-query";
import { DraftCalendarTable } from "./components/DraftCalendarTable";
import { MeetingFlyout } from "./components/MeetingFlyout";
import { BulkActionMenu } from "./components/BulkActionMenu";
import { ImportantDatesDialog } from "./components/ImportantDatesDialog";
import { TableauMeetingDetails } from "./components/TableauMeetingDetails";
import { BulkChangeStatusDialog, BULK_STATUS_CONSTANTS, type BulkChangeStatusValues } from "./components/BulkChangeStatusDialog";
import { BulkPushToSummitResultDialog } from "./components/BulkPushToSummitResultDialog";
import { SubmitCalendarDialog } from "./components/SubmitCalendarDialog";
import { CloseCalendarDialog } from "./components/CloseCalendarDialog";
import { PushToSummitConfirmDialog } from "./components/PushToSummitConfirmDialog";
import { BulkEditFlyout } from "./components/BulkEditFlyout";
import { BulkAddCommentDialog } from "./components/BulkAddCommentDialog";
import { BulkEditErrorToast } from "./components/BulkEditErrorToast";
import type { BulkEditFailedMeeting } from "@/types/draftCalendar";
import { CopyToNewDialog } from "./components/CopyToNewDialog";
import type {
  DraftCalendarItem,
  DraftMeetingResponse,
  BulkActionInValidStatusResponse,
  BulkDraftPushToSummitRequest,
  BulkUpdateStatusRequest,
  CheckUserBusinessMapResponse
} from "@/types/draftCalendar";

export function DraftCalendarPage() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const userIdEmail = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("Draft Calendar", userIdEmail);
  }, [userIdEmail]);

  const isAdmin = useIsAdmin();
  const { hasPermission } = usePermissions();
  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const canBulkAction = useCanPerformBulkAction();
  const canToggleSettings = useCanViewToggleSettings();
  const canSubmit = hasPermission("CanSubmitToApprover");
  // Use roleCode as the source of truth for role checks.
  const userRoleCode = (currentUser?.roleCode ?? "").toLowerCase();
  const isScheduler = userRoleCode === "scheduler";
  const userRole = userRoleCode;
  const isApprover = userRoleCode === "business_approver";
  const isBusinessReviewer = userRoleCode === "business_reviewer";
  const isBusinessApprover = isApprover;
  const userId = currentUser?.id ?? 0;

  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const { data: businessesData } = useDraftBusinesses();
  const { data: meetingTypesMapAll = {} } = useDraftMeetingTypes();

  // Program Owner (Business Approver) is restricted to businesses/meeting types mapped
  // to them via the user permission API (currentUser.businesses), instead of the full list.
  const mappedBusinessTypes = currentUser?.businesses;
  const businesses: Record<string, string[]> = useMemo(() => {
    const all = businessesData ?? {};
    if (!isBusinessApprover || !mappedBusinessTypes) return all;
    const mappedBusinessNames = new Set(
      Object.entries(mappedBusinessTypes)
        .filter(([, types]) => (types ?? []).some((t) => t.isMapped))
        .map(([name]) => name)
    );
    return Object.fromEntries(Object.entries(all).filter(([name]) => mappedBusinessNames.has(name)));
  }, [businessesData, isBusinessApprover, mappedBusinessTypes]);

  const meetingTypesMap: Record<string, string[]> = useMemo(() => {
    if (!isBusinessApprover || !mappedBusinessTypes) return meetingTypesMapAll;
    const mappedMeetingTypeNames = new Set(
      Object.values(mappedBusinessTypes).flatMap((types) => (types ?? []).filter((t) => t.isMapped).map((t) => t.meetingTypeName))
    );
    return Object.fromEntries(Object.entries(meetingTypesMapAll).filter(([name]) => mappedMeetingTypeNames.has(name)));
  }, [meetingTypesMapAll, isBusinessApprover, mappedBusinessTypes]);

  // Business → mapped meeting type names, so the Meeting Type dropdown can be scoped to the selected Business
  const mappedBusinessMeetingTypes: Record<string, string[]> | undefined = useMemo(() => {
    if (!isBusinessApprover || !mappedBusinessTypes) return undefined;
    return Object.fromEntries(
      Object.entries(mappedBusinessTypes).map(([business, types]) => [
        business,
        (types ?? []).filter((t) => t.isMapped).map((t) => t.meetingTypeName)
      ])
    );
  }, [isBusinessApprover, mappedBusinessTypes]);

  const { data: attendeeLevelsData } = useAttendeeLevels();
  const attendeeLevels: string[] = attendeeLevelsData ?? [];
  const { data: locationsData } = useLocations();
  const locations: string[] = locationsData ?? [];
  const { data: duDeclinedReasonsData } = useDUDeclinedReasons();
  const duDeclinedReasons: string[] = duDeclinedReasonsData ?? [];
   

  const defaultFY = useMemo(
    () => fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0],
    [fiscalYears]
  );
  const [selectedFYId, setSelectedFYId] = useState<number>(0);
  const activeFYId = selectedFYId || defaultFY?.id || 0;
  const activeFY = fiscalYears.find((fy) => fy.id === activeFYId);
  const isCalendarClosed = activeFY?.submissionStatus === "Closed";
  const isCalendarSubmitted = activeFY?.submissionStatus === "Submitted";
  const shouldShowTableauMeetingDetails = (isCalendarClosed || !isCalendarSubmitted) && (isBusinessApprover || isBusinessReviewer);
  const tableauFyYear = fiscalYears[1]?.code ?? fiscalYears[0]?.code ?? "";
  const { data: draftItems = [], isPending, isFetching, refetch } =
    useDraftCalendar(activeFYId, userEmail);

  // Mirror Blazor GetSubmittedCalendarStatus — only for Admin/Scheduler (same as Blazor LoadDraftCalender)
  const { data: calendarStatus } = useCalendarSubmissionStatus(activeFYId);
  // isCalendarSubmitted = Blazor "isSubmitted" — true when calendar is Submitted or Closed
  const calendarStatusVal = calendarStatus?.calendarStatus ?? "";
  //const isCalendarSubmitted = calendarStatusVal === "Submitted" || calendarStatusVal === "Closed";
  // IsCloseCalendarEnable = true only when status is exactly "Submitted" (not yet Closed)
  const isCloseCalendarEnabled = calendarStatusVal === "Submitted";

  // In TanStack Query v5, disabled queries return isPending=true permanently.
  // Only show the loading spinner when the query is actually fetching data.
  const isCalendarLoading = isPending && isFetching;

  const isFYClosed = isCalendarClosed;

  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [flyoutMeetingId, setFlyoutMeetingId] = useState<number | null>(null);
  const [flyoutViewOnly, setFlyoutViewOnly] = useState(true);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [copyFromData, setCopyFromData] = useState<DraftMeetingResponse | null>(null);
  const [showCopyToNewDialog, setShowCopyToNewDialog] = useState(false);
  const [copySelectedFields, setCopySelectedFields] = useState<Set<string> | undefined>(undefined);

  const updateShowInGRC = useUpdateShowInGRC();
  const bulkUpdateStatus = useBulkUpdateStatus();
  const bulkUpdateStates = useBulkUpdateStates();
  const bulkDraftPushToSummit = useBulkDraftPushToSummit();
  const trackUserViewedComments = useTrackUserViewedComments();
  const setDraftRead = useSetDraftRead();
  const checkDraftBusiness = useCheckDraftBusiness();
  const closeDraftCalendar = useCloseDraftCalendar();
  const bulkUpdateFields = useBulkUpdateDraftFields();
  const bulkAddComment = useBulkAddComment();

  // ── Bulk Add Comment Dialog ───────────────────────────────────────────────
  const [showBulkAddCommentDialog, setShowBulkAddCommentDialog] = useState(false);

  // ── Bulk Edit Flyout ──────────────────────────────────────────────────────
  const [showBulkEditFlyout, setShowBulkEditFlyout] = useState(false);
  const [highlightedMeetingIds, setHighlightedMeetingIds] = useState<Set<number>>(new Set());
  const [highlightedFields, setHighlightedFields] = useState<Set<string>>(new Set());
  const [bulkEditSuccessMessage, setBulkEditSuccessMessage] = useState<string | null>(null);
  const [bulkEditFailedMeetings, setBulkEditFailedMeetings] = useState<BulkEditFailedMeeting[]>([]);
  const [bulkEditFailedTotal, setBulkEditFailedTotal] = useState(0);
  const bulkEditFailedIds = useMemo(() => new Set(bulkEditFailedMeetings.map((m) => m.id)), [bulkEditFailedMeetings]);
  // Track the count of rows visible after all table-level filters (search + column checkboxes)
  const [tableFilteredCount, setTableFilteredCount] = useState<number | null>(null);
  const [tableFilteredItems, setTableFilteredItems] = useState<DraftCalendarItem[] | null>(null);
  const handleFilteredCountChange = useCallback((count: number) => setTableFilteredCount(count), []);
  const handleFilteredItemsChange = useCallback((items: DraftCalendarItem[]) => setTableFilteredItems(items), []);

  function clearBulkEditSuccess() {
    setHighlightedMeetingIds(new Set());
    setHighlightedFields(new Set());
    setBulkEditSuccessMessage(null);
    setBulkEditFailedMeetings([]);
    setBulkEditFailedTotal(0);
  }

  // ── Submit Calendar state ──────────────────────────────────────────────────
  const [showSubmitCalendarDialog, setShowSubmitCalendarDialog] = useState(false);
  const [submitCalendarLoading, setSubmitCalendarLoading] = useState(false);
  const [submitCalendarResults, setSubmitCalendarResults] = useState<CheckUserBusinessMapResponse[]>([]);
  const [submitCalendarConfigured, setSubmitCalendarConfigured] = useState(false);
  const [submittingCalendar, setSubmittingCalendar] = useState(false);

  async function openSubmitCalendarDialog() {
    setShowSubmitCalendarDialog(true);
    setSubmitCalendarLoading(true);
    setSubmitCalendarResults([]);
    // Mirror Blazor GroupMeetingTypesByBusiness: group by Business + MeetingType
    const businesses = Array.from(
      new Map(
        draftItems
          .filter((i) => i.business && i.meetingType)
          .map((i) => [`${i.business}|${i.meetingType}`, { business: i.business!, meetingType: i.meetingType! }])
      ).values()
    );
    try {
      const results = await checkDraftBusiness.mutateAsync({ businesses });
      setSubmitCalendarResults(results);
      setSubmitCalendarConfigured(results.length > 0 && results.every((r) => r.isConfigured?.toLowerCase() === "configured"));
    } catch {
      toast.error("Failed to check business configurations. Please try again.");
      setShowSubmitCalendarDialog(false);
    } finally {
      setSubmitCalendarLoading(false);
    }
  }

  async function handleSubmitCalendarConfirm() {
    setSubmittingCalendar(true);
    try {
      // Mirror Blazor OnDraftCalendarSubmit:
      // Group draftItems by Business+MeetingType, then collect IDs for groups
      // whose business appears in the checked submitCalendarResults.
      const groupedDrafts = Object.values(
        draftItems
          .filter((i) => i.business && i.meetingType)
          .reduce<Record<string, typeof draftItems>>((acc, item) => {
            const key = `${item.business}|${item.meetingType}`;
            (acc[key] ??= []).push(item);
            return acc;
          }, {})
      );

      const idsToUpdate = groupedDrafts
        .filter((group) =>
          submitCalendarResults.some((r) => r.business === group[0].business)
        )
        .flatMap((group) => group.map((i) => i.id));

      if (idsToUpdate.length > 0) {
        const result = await bulkUpdateStates.mutateAsync({
          draftIds: idsToUpdate,
          state: Statuses.NewRequest,
          fYId: activeFY?.id ?? 0, 
        });

        if (result) {
          toast.success("Calendar submitted successfully.");
          setShowSubmitCalendarDialog(false);
          await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else {
        toast.error("No eligible meetings found to submit.");
      }
    } catch {
      toast.error("Failed to submit calendar. Please try again.");
    } finally {
      setSubmittingCalendar(false);
    }
  }

  const [showCloseCalendarDialog, setShowCloseCalendarDialog] = useState(false);
  const [closingCalendar, setClosingCalendar] = useState(false);

  function handleCloseCalendar() {
    setShowCloseCalendarDialog(true);
  }

  async function handleCloseCalendarConfirm() {
    setClosingCalendar(true);
    try {
      const result = await closeDraftCalendar.mutateAsync({ fYID: activeFYId });
      if (result) {
        toast.success("Draft calendar closed successfully.");
        setShowCloseCalendarDialog(false);
        await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      } else {
        toast.error("Failed to close draft calendar. Please try again later.");
      }
    } catch {
      toast.error("Failed to close draft calendar. Please try again later.");
    } finally {
      setClosingCalendar(false);
    }
  }

  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [pendingPushPayload, setPendingPushPayload] = useState<BulkDraftPushToSummitRequest | null>(null);
  const [showPushResult, setShowPushResult] = useState(false);
  const [pushTotalCount, setPushTotalCount] = useState(0);
  const [pushSuccessCount, setPushSuccessCount] = useState(0);
  const [failedMeetings, setFailedMeetings] = useState<BulkActionInValidStatusResponse[]>([]);

  function openCreate() { setCopyFromData(null); setFlyoutMeetingId(0); setFlyoutViewOnly(false); setLockedBy(null); }
  function openView(item: DraftCalendarItem) {
    setFlyoutMeetingId(item.id);
    setFlyoutViewOnly(true);
    setLockedBy(null);
    // Mirror Blazor OnRowClickHandler: mark draft as read if not already
    if (!item.isRead) {
      queryClient.setQueryData<DraftCalendarItem[]>(
        draftCalendarKeys.list(activeFYId, userEmail),
        (old) => old?.map((i) => i.id === item.id ? { ...i, isRead: true, isReadText: "Read" } : i)
      );
      void setDraftRead.mutateAsync({ draftId: item.id, email: userEmail }).catch(() => {
        // Roll back optimistic update on failure
        queryClient.setQueryData<DraftCalendarItem[]>(
          draftCalendarKeys.list(activeFYId, userEmail),
          (old) => old?.map((i) => i.id === item.id ? { ...i, isRead: false, isReadText: "Unread" } : i)
        );
        console.error("[DraftCalendar] Failed to mark draft as read:", item.id);
      });
    }
    // Mirror Blazor OnRowClickHandler: if there are unseen comments, track that the user viewed them
    if (item.unSeenComments) {
      // Optimistically clear the badge in the cache immediately
      queryClient.setQueryData<DraftCalendarItem[]>(
        draftCalendarKeys.list(activeFYId, userEmail),
        (old) => old?.map((i) => i.id === item.id ? { ...i, unSeenComments: false } : i)
      );
      // Fire-and-forget the tracking call (mirrors Blazor's non-blocking await)
      void trackUserViewedComments.mutateAsync({ softHoldId: item.id, userId: userId });
    }
  }
  function handleFlyoutSaved() { setFlyoutMeetingId(null); setCopyFromData(null); setCopySelectedFields(undefined); void refetch(); }
  function handleFlyoutClose() { setFlyoutMeetingId(null); setCopyFromData(null); setCopySelectedFields(undefined); }

  function handleCopyToNew() {
    if (!flyoutMeetingId || flyoutMeetingId === 0) return;
    const data = queryClient.getQueryData<DraftMeetingResponse>(draftCalendarKeys.meeting(flyoutMeetingId));
    setCopyFromData(data ?? null);
    setShowCopyToNewDialog(true);
  }

  function handleCopyConfirm(selectedFields: Set<string>) {
    setShowCopyToNewDialog(false);
    setCopySelectedFields(selectedFields);
    setFlyoutMeetingId(0);
    setFlyoutViewOnly(false);
    setLockedBy(null);
  }

  function handlePushToSummitSingle() {
    if (!flyoutMeetingId || flyoutMeetingId === 0) return;
    const singleId = flyoutMeetingId;
    setFlyoutMeetingId(null);
    setCopyFromData(null);
    setPendingPushPayload({ draftIds: [singleId], status: Statuses.Resolved, userId });
    setShowPushConfirm(true);
  }

  async function handleToggleGRC(id: number, showInGRC: boolean) {
    // Guard: mirrors Blazor OnToggleShowInGRCHandler – invalid ID check
    if (id <= 0) { toast.error("Cannot update GRC visibility: Invalid ID"); return; }
    // Guard: Removed / DU Declined meetings cannot be updated (mirrors Blazor BulkActionShowInGRC filter)
    const item = draftItems.find((i) => i.id === id);
    if (item?.state === "Removed" || item?.state === "DU Declined") {
      toast.error("Cannot update GRC visibility: meeting is in Removed or DU Declined state.");
      return;
    }
    try {
      const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: [id], showInGRC });
      if (updatedIds.length > 0) {
        queryClient.setQueryData<DraftCalendarItem[]>(
          draftCalendarKeys.list(activeFYId, userEmail),
          (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC } : i)
        );
      } else {
        toast.error("Update failed. Please try again.");
      }
    } catch {
      toast.error("Update failed. Please try again.");
    }
  }

  async function handleBulkAction(actionId: string) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    if (selectedItems.length === 0) { toast.error("Select at least one meeting."); return; }
    try {
      if (actionId === "add-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);
        if (eligibleIds.length === 0) { toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state."); return; }
        const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: true });
        // Update cache for confirmed IDs only (green toggle), mirrors Blazor result.ForEach
        if (updatedIds.length > 0) {
          queryClient.setQueryData<DraftCalendarItem[]>(
            draftCalendarKeys.list(activeFYId, userEmail),
            (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC: true } : i)
          );
          toast.success("Added to GRC.");
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else if (actionId === "remove-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);
        if (eligibleIds.length === 0) { toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state."); return; }
        const updatedIds = await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: false });
        // Update cache for confirmed IDs only (grey toggle)
        if (updatedIds.length > 0) {
          queryClient.setQueryData<DraftCalendarItem[]>(
            draftCalendarKeys.list(activeFYId, userEmail),
            (old) => old?.map((i) => updatedIds.includes(i.id) ? { ...i, showInGRC: false } : i)
          );
          toast.success("Removed from GRC.");
        } else {
          toast.error("Update failed. Please try again.");
        }
      } else if (actionId === "change-status") {
        setShowStatusDialog(true); return;
      }
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
    } catch {
      toast.error("Bulk action failed. Please try again.");
    }
  }

  function getBulkStatusOptions() {
    const roleOptions: Record<string, string[]> = {
      admin:    [Statuses.ProgramOwnerReviewed, Statuses.DUReview, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed],
      scheduler:[Statuses.ProgramOwnerReviewed, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed],
      business_approver:[Statuses.ProgramOwnerReviewed, Statuses.Removed],
      business_reviewer:[Statuses.ProgramOwnerReviewed, Statuses.Removed],
      view_only:      [Statuses.NewRequest, Statuses.ProgramOwnerReviewed,  Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed]
    };
    const roleScoped = roleOptions[userRole];
    if (roleScoped) return roleScoped;
    const existingStates = draftItems.map((item) => item.state).filter((s): s is string => !!s && s.trim().length > 0);
    const fallbackStates = [Statuses.ProgramOwnerReviewed, Statuses.Resolved, BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED, BULK_STATUS_CONSTANTS.STATUS_REMOVED];
    return Array.from(new Set([...existingStates, ...fallbackStates]));
  }

  async function handleStatusDialogSubmit(values: BulkChangeStatusValues) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    const selectedIds = selectedItems.map((item) => item.id);
    const isApproverScopedRole = isBusinessApprover || isBusinessReviewer;
    const approverEligibleCurrentStatusesByTarget: Record<string, string[]> = {
      "Program Owner Reviewed": ["New Request"],
      Removed: ["New Request"]
    };
    let statusEligibleItems = selectedItems;
    if (isApproverScopedRole) {
      const allowedCurrentStates = approverEligibleCurrentStatusesByTarget[values.status] ?? [];
      if (allowedCurrentStates.length > 0) {
        const allowedSet = new Set(allowedCurrentStates.map((s) => s.toLowerCase()));
        statusEligibleItems = selectedItems.filter((item) => allowedSet.has((item.state ?? "").trim().toLowerCase()));
      }
    }
    const draftIds = statusEligibleItems.filter((item) => item.state !== values.status).map((item) => item.id);
    if (!draftIds.length) { toast.error("No Meeting(s) are there to update please try again."); return; }
    if (values.status === BULK_STATUS_CONSTANTS.STATUS_RESOLVED) {
      setPendingPushPayload({ draftIds: selectedIds, status: values.status, comment: values.comment || "", userId });
      setShowStatusDialog(false); setShowPushConfirm(true); return;
    }
    const payload: BulkUpdateStatusRequest = { draftIds, status: values.status, comment: values.comment || "", duDeclineReason: values.duDeclineReason || "", duDeclineComments: values.duDeclineComments || "", userId };
    const updatedCount = await bulkUpdateStatus.mutateAsync(payload);
    if (updatedCount <= 0) { toast.error("No Meeting(s) are there to update please try again."); return; }
    const skipped = selectedItems.length - draftIds.length;
    const skippedPrefix = skipped > 0 ? (values.status === BULK_STATUS_CONSTANTS.STATUS_REMOVED ? `${skipped} items have been skipped. ` : `${skipped} Meeting(s) could not be updated due to their current status. `) : "";
    toast.success(`${skippedPrefix}${updatedCount} Meeting(s) has been updated successfully.`);
    setShowStatusDialog(false); setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handlePushToSummitConfirm() {
    if (!pendingPushPayload) return;
    const result = await bulkDraftPushToSummit.mutateAsync(pendingPushPayload);
    setShowPushConfirm(false);
    setPushTotalCount(pendingPushPayload.draftIds.length);
    setPushSuccessCount(result.successCount || 0);
    setFailedMeetings(result.bulkActionInValidStatusResponses || []);
    setShowPushResult(true); setPendingPushPayload(null); setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handleBulkFieldsSave(request: import("@/types/draftCalendar").BulkUpdateDraftFieldsRequest) {
    try {
      const allIds = request.draftIds;
      request = { ...request, role: userRole };

      // Separate GRC from field updates — they use different APIs
      const grcValue = request.showInGRC;
      const { showInGRC: _grc, ...fieldsRequest } = request;
      const hasFieldChanges = Object.keys(fieldsRequest).some(
        (k) => k !== "draftIds" && k !== "userId" && k !== "role" && fieldsRequest[k as keyof typeof fieldsRequest] != null
      );

      // When status is "Push to Summit" (Resolved), route through push-to-summit API
      if (fieldsRequest.draftEventState === Statuses.Resolved) {
        const hasOtherFields = Object.keys(fieldsRequest).some(
          (k) => k !== "draftIds" && k !== "userId" && k !== "role" && k !== "draftEventState" && fieldsRequest[k as keyof typeof fieldsRequest] != null
        );
        if (hasOtherFields) {
          const { draftEventState: _status, ...fieldsOnly } = fieldsRequest;
          await bulkUpdateFields.mutateAsync({ ...fieldsOnly, draftIds: fieldsRequest.draftIds, userId: fieldsRequest.userId, role: userRole });
        }
        // TODO:: move this logic into the bulkUpdateFields API so we can return a single result object instead of calling two APIs
        if (grcValue != null) await updateShowInGRC.mutateAsync({ softHoldIds: allIds, showInGRC: grcValue });
        const pushPayload = { draftIds: fieldsRequest.draftIds, status: Statuses.Resolved, comment: "", userId };
        const result = await bulkDraftPushToSummit.mutateAsync(pushPayload);
        setPushTotalCount(fieldsRequest.draftIds.length);
        setPushSuccessCount(result.successCount || 0);
        setFailedMeetings(result.bulkActionInValidStatusResponses || []);
        setShowPushResult(true);
        setShowBulkEditFlyout(false);
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
        return;
      }

      let updatedCount = 0;
      let skippedMeetings: BulkEditFailedMeeting[] = [];       

      if (hasFieldChanges) {
        const result = await bulkUpdateFields.mutateAsync(fieldsRequest);
        updatedCount = result.updatedCount;
        skippedMeetings = (result.failedMeetings ?? []).map((f) => ({
          id: f.id,
          priorId: f.priorId ?? null,
          meetingName: f.meetingName ?? null,
          date: f.date ?? null,
          reason: f.errorMessage,
        }));
      }

      const editableIds = allIds.filter((id) => !skippedMeetings.some((m) => m.id === id));

      if (editableIds.length === 0 && skippedMeetings.length > 0) {
        setBulkEditFailedMeetings(skippedMeetings);
        setBulkEditFailedTotal(allIds.length);
        setShowBulkEditFlyout(false);
        return;
      }
      // TODO:: move this logic into the bulkUpdateFields API so we can return a single result object instead of calling two APIs
      if (grcValue != null && editableIds.length > 0) await updateShowInGRC.mutateAsync({ softHoldIds: editableIds, showInGRC: grcValue });

      if (updatedCount > 0 || grcValue != null) {
        const savedCount = updatedCount > 0 ? updatedCount : editableIds.length;
        const updatedCols = new Set(
          Object.entries(BULK_REQUEST_TO_TABLE_COL)
            .filter(([field]) => field in fieldsRequest && fieldsRequest[field as keyof typeof fieldsRequest] !== undefined && fieldsRequest[field as keyof typeof fieldsRequest] !== null)
            .map(([, col]) => col)
            .filter((col): col is DcTableColKey => col !== undefined)
        );
        if (grcValue != null) updatedCols.add("showInGRC" as DcTableColKey);
        setHighlightedFields(updatedCols);

        if (skippedMeetings.length > 0) {
          const failedIdSet = new Set(skippedMeetings.map((m) => m.id));
          setHighlightedMeetingIds(new Set(editableIds.filter((id) => !failedIdSet.has(id))));
          setBulkEditFailedMeetings(skippedMeetings);
          setBulkEditFailedTotal(allIds.length);
          setBulkEditSuccessMessage(null);
        } else {
          setHighlightedMeetingIds(new Set(editableIds));
          setBulkEditSuccessMessage(`Successfully saved ${savedCount} meeting(s).`);
          setBulkEditFailedMeetings([]);
          setBulkEditFailedTotal(0);
        }

        setShowBulkEditFlyout(false);
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      } else {
        toast.error("No meetings were updated. They may be locked or in a non-editable state.");
      }
    } catch {
      toast.error("Failed to update meetings. Please try again.");
    }
  }

  // Adds the same comment to all selected meetings, regardless of status, role, or state.
  // Comments added this way are not tracked in meeting history.
  async function handleBulkAddComment(comment: string) {
    const draftIds = Array.from(selected);
    if (draftIds.length === 0) { toast.error("Select at least one meeting."); return; }
    try {
      const updatedCount = await bulkAddComment.mutateAsync({ draftIds, comment, userId, createdBy: userEmail, userName: accounts[0]?.name ?? userEmail });
      if (updatedCount > 0) {
        toast.success(`Comment added to ${updatedCount} meeting${updatedCount > 1 ? "s" : ""}.`);
        setShowBulkAddCommentDialog(false);
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      } else {
        toast.error("Failed to add comment. Please try again.");
      }
    } catch {
      toast.error("Failed to add comment. Please try again.");
    }
  }

  const bulkActions = useMemo(() => {
    const actions = [];
    if (canToggleSettings) {
      actions.push({ id: "add-grc", label: "Add to GRC" });
      actions.push({ id: "remove-grc", label: "Remove from GRC" });
    }
    actions.push({
      id: "change-status",
      label: "Change Status",
    });
    return actions;
  }, [canToggleSettings]);

  function handleAddMeetingClick() {
    openCreate();
  }

  const filteredItems = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? draftItems.filter((i) => i.meetingName?.toLowerCase().includes(q) || i.business?.toLowerCase().includes(q) || i.meetingId?.toLowerCase().includes(q))
      : draftItems;
  }, [draftItems, searchTerm]);

  const filteredCount = tableFilteredCount ?? filteredItems.length;

  return (
    <main className="draft-calendar-scroll-page flex flex-col" aria-label="Draft Calendar">
      {/* Calendar Closed banner
           Mirrors Blazor TelerikWindow (.no-header-window.close-calendar):
           visible in list view when the fiscal year is Closed.
           Format: "ⓘClosed by <user> on <date> <time> CT" */}
      {isFYClosed && viewMode === "list" && (() => {
        const closedBy = calendarStatus?.updatedBy ?? "";
        const rawDate = calendarStatus?.calendarSubmissionDate ?? "";
        let closedOnStr = "";
        if (rawDate) {
          try {
            const d = parseISO(rawDate);
            if (isValid(d)) {
              // "07/07/2026 4:57 AM CT"
              closedOnStr = format(d, "MM/dd/yyyy h:mm aa") + " CT";
            }
          } catch { /* ignore */ }
        }
        return (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "fixed",
              top: 50,
              left: "43%",
              transform: "translateX(-50%)",
              zIndex: 100,
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              backgroundColor: "#FFFCF0",
              height: 40,
              display: "flex",
              alignItems: "center",
              padding: "0 16px",
              gap: 6,
              fontSize: 13,
              color: "#000",
              whiteSpace: "nowrap",
              border: "1px solid #FFE082"
            }}
          >
            {/* ⓘ info character */}
            <span style={{ fontSize: 16, lineHeight: 1, color: "#1C1C1C", flexShrink: 0 }}>&#9432;</span>
            <span>
              Closed by{closedBy ? <> <strong>{closedBy}</strong></> : ""}
              {closedOnStr ? <> on {closedOnStr}</> : ""}
            </span>
          </div>
        );
      })()}
      {/* ── Toolbar ── */}
      
      {!shouldShowTableauMeetingDetails && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        {/* Search */}
        
        <div className="relative">
          <label htmlFor="dc-search" className="sr-only">Search meetings</label>
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input id="dc-search" type="text" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search meetings"
            className="h-8 w-64 border border-slate-300 pl-8 pr-8 text-sm focus:outline-none focus:border-slate-500" />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm("")} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Export */}
          {viewMode === "list" && (
            <button
              type="button"
              aria-label="Export to Excel"
              className="flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-100"
              title="Export list to Excel"
              onClick={() => exportDraftCalendarToExcel(tableFilteredItems ?? filteredItems, { isAdmin, isScheduler, canToggleSettings }).catch(() => toast.error("Export failed"))}
            >
              <Download className="h-4 w-4" />
            </button>
          )}

          {/* List / Calendar view toggle */}
          <div className="flex overflow-hidden border border-slate-300">
            <button type="button" onClick={() => setViewMode("list")} aria-label="List view"
              aria-pressed={viewMode === "list"}
              className={`flex h-8 w-8 items-center justify-center ${viewMode === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <List className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => navigate("/calendarview")}
              aria-label="Calendar view" aria-pressed={false}
              className="flex h-8 w-8 items-center justify-center border-l border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>

          {/* Bulk Actions — hidden for Business Approver / Business Reviewer (Program Owner) */}
          {/* {canBulkAction && !isBusinessApprover && !isBusinessReviewer && (
            <BulkActionMenu actions={bulkActions} onAction={handleBulkAction} disabled={selected.size === 0} />
          )} */}

          {/* Bulk Add Comment button — available to any user with access, regardless of status/role/state; only shown once meetings are selected */}
          {selected.size > 0 && (
            <button type="button" onClick={() => setShowBulkAddCommentDialog(true)}
              className="border border-slate-900 bg-white px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-slate-100">
              {`Add ${selected.size} Comment${selected.size > 1 ? "s" : ""}`}
            </button>
          )}

          {/* Bulk Edit button — shown when meetings are selected */}
          {canBulkAction && (
            <button type="button" onClick={() => setShowBulkEditFlyout(true)}
              disabled={selected.size === 0}
              className="border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
              {selected.size > 0 ? `Edit ${selected.size} Meeting${selected.size > 1 ? "s" : ""}` : "Select Meetings Below to Edit"}
            </button>
          )}

          {/* Fiscal year selector */}
          {!(isApprover || isBusinessReviewer) && (<div className="flex items-center gap-1.5">
            <label htmlFor="fy-select" className="sr-only">Fiscal Year</label>
            {loadingFY ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : (
              <select id="fy-select" value={activeFYId}
                onChange={(e) => {
                    const newFYId = Number(e.target.value);
                    // Remove cached data for the new FY so stale data from a previous
                    // load of that FY is never shown — forces a fresh fetch + loading spinner
                    queryClient.removeQueries({ queryKey: draftCalendarKeys.list(newFYId, userEmail) });
                    setSelectedFYId(newFYId);
                    setSelected(new Set());
                  }}
                className="FY-dropdown">
                {fiscalYears.map((fy) => (
                  <option key={fy.id} value={fy.id}>
                    {fy.code}{fy.submissionStatus === "Closed" ? " (Closed)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          )}

          {/* Add Meeting — show when user has permission OR while permissions are still loading */}
          {(!currentUser || canAddMeeting) && !isFYClosed && (
            <button type="button" onClick={handleAddMeetingClick}
              className="Add-Request-button border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
                Add Meeting
            </button>
          )}

          {/* Calendar Options */}
          {canToggleSettings && (
            <CalendarOptionsMenu canSubmit={canSubmit} isFYClosed={isFYClosed}
              isCalendarSubmitted={isCalendarSubmitted}
              isCloseCalendarEnabled={isCloseCalendarEnabled}
              onSubmitCalendar={() => void openSubmitCalendarDialog()}
              onCloseCalendar={() => void handleCloseCalendar()} />
          )}
        </div>
        </div>
         )}

      {/* ── Status bar ── */}
      {!shouldShowTableauMeetingDetails && (
        <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
          <span>
            {selected.size > 0 ? `${selected.size} selected of ${filteredCount}` : `${filteredCount} meeting${filteredCount !== 1 ? "s" : ""}`}
          </span>
          {isFetching && !isPending && (
            <span className="flex items-center gap-1 text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" /> Refreshing&hellip;
            </span>
          )}
          {isFYClosed && <span className="rounded bg-red-100 px-2 py-0.5 text-red-700 font-medium">Fiscal Year Closed</span>}
        </div>
      )}

      {/* ── Content ── */}
      <div className="min-h-0 flex-1 overflow-hidden px-0">
        {isCalendarLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading calendar&hellip;</span>
          </div>
        ) : shouldShowTableauMeetingDetails ? (
          <TableauMeetingDetails FyYear={tableauFyYear} />
        ) : viewMode === "calendar" ? (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">Calendar view coming soon.</div>
        ) : (
          <DraftCalendarTable items={draftItems} searchTerm={searchTerm} selected={selected}
            onSelectionChange={setSelected} onRowClick={openView} canEdit={canEditMeeting}
            canBulkAction={canBulkAction} canToggleSettings={canToggleSettings}
            signOffProcessStarted={false} isAdmin={isAdmin} isScheduler={isScheduler}
            onToggleGRC={canToggleSettings ? handleToggleGRC : undefined}
            highlightedIds={highlightedMeetingIds}
            highlightedFields={highlightedFields}
            failedIds={bulkEditFailedIds}
            onFilteredCountChange={handleFilteredCountChange}
            onFilteredItemsChange={handleFilteredItemsChange} />
        )}
      </div>

      {/* ── Footer bar ── */}
      {!shouldShowTableauMeetingDetails && (
        <div className="flex items-center justify-between border-t border-slate-100 bg-white px-4 py-1.5">
          <div className="flex items-center gap-4">
          <a href="#" onClick={(e) => { e.preventDefault(); setShowHolidayDialog(true); }}
              className="holiday-link">
              View Important Dates &amp; Holidays
            </a>
            {/* Core/Secondary legend — mirrors Blazor: hidden for Approver/BusinessReviewer roles */}
          {userRole !== "business_approver" && userRole !== "business_reviewer" && (
            <div className="flex items-center gap-3 text-xs">
              <span className="event-status-draft-legend-Core" />
              <span className="event-status-legend">Core</span>
              <span className="event-status-draft-legend-Secondary" />
              <span className="event-status-legend">Secondary</span>
            </div>
          )}
        </div>
        <span className="total-count">Total Count: {filteredCount}</span>
        </div>
      )}

      {/* ── Bulk Edit success banner — centered, persists until user closes ── */}
      {bulkEditSuccessMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: 64,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            backgroundColor: "#A8D09E",
            border: "1px solid #7CB97B",
            display: "flex",
            alignItems: "center",
            padding: "10px 16px",
            gap: 10,
            fontSize: 14,
            color: "#1C3D1A",
            whiteSpace: "nowrap",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM7.5 12.75L3.75 9L4.8075 7.9425L7.5 10.6275L13.1925 4.935L14.25 6L7.5 12.75Z" fill="#1C3D1A" />
          </svg>
          <span>{bulkEditSuccessMessage}</span>
          <button
            type="button"
            onClick={clearBulkEditSuccess}
            aria-label="Dismiss success message"
            style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", color: "#1C3D1A" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {bulkEditFailedMeetings.length > 0 && (
        <BulkEditErrorToast
          failedCount={bulkEditFailedMeetings.length}
          totalCount={bulkEditFailedTotal}
          failedMeetings={bulkEditFailedMeetings}
          onClose={clearBulkEditSuccess}
        />
      )}

      <BulkChangeStatusDialog open={showStatusDialog} statuses={getBulkStatusOptions()}
        onClose={() => setShowStatusDialog(false)} onSubmit={handleStatusDialogSubmit}
        submitting={bulkUpdateStatus.isPending || bulkDraftPushToSummit.isPending} />

      {showPushConfirm && (
        <PushToSummitConfirmDialog
          open={showPushConfirm}
          message="Are you sure you want to push selected meetings to Summit?"
          submitting={bulkDraftPushToSummit.isPending}
          onCancel={() => setShowPushConfirm(false)}
          onConfirm={() => void handlePushToSummitConfirm()}
        />
      )}

      <BulkPushToSummitResultDialog open={showPushResult} onClose={() => setShowPushResult(false)}
        totalCount={pushTotalCount} successCount={pushSuccessCount} failedMeetings={failedMeetings} />

      {flyoutMeetingId !== null && (() => {
        const flyoutItem = draftItems.find((i) => i.id === flyoutMeetingId);
        return (
        <MeetingFlyout meetingId={flyoutMeetingId} fiscalYear={activeFY} businesses={businesses}
          meetingTypes={meetingTypesMap} mappedBusinessMeetingTypes={mappedBusinessMeetingTypes}
          attendeeLevels={attendeeLevels} locations={locations}
          duDeclinedReasons={duDeclinedReasons}
          isViewOnly={flyoutViewOnly} lockedBy={lockedBy}
          canEdit={canEditMeeting && (flyoutItem?.state ?? null) !== Statuses.CreatedInSummit}
          canCopyToNew={canEditMeeting}
          canSubmit={canSubmit}
          isBusinessApprover={isBusinessApprover}
          isBusinessReviewer={isBusinessReviewer}
          canViewSignOffRequest={false}
          canSubmitSignOff={false}
          canEditSignOffStatus={false}
          signOffStatus={null}
          copyFromData={copyFromData ?? undefined}
          copySelectedFields={copySelectedFields}
          canPushToSummit={false}
          currentUserName={currentUser?.name ?? ""}
          currentUserEmail={userEmail}
          onEdit={() => setFlyoutViewOnly(false)} onSaved={handleFlyoutSaved} onClose={handleFlyoutClose}
          onBack={() => setFlyoutViewOnly(true)}
          onCopyToNew={handleCopyToNew}
          onPushToSummit={handlePushToSummitSingle} />
        );
      })()}

      {showCopyToNewDialog && copyFromData && (
        <CopyToNewDialog
          isAdminOrScheduler={isAdmin || isScheduler}
          onCopy={handleCopyConfirm}
          onClose={() => setShowCopyToNewDialog(false)}
        />
      )}

      <ImportantDatesDialog open={showHolidayDialog} onClose={() => setShowHolidayDialog(false)} />

      <SubmitCalendarDialog
        open={showSubmitCalendarDialog}
        loading={submitCalendarLoading}
        results={submitCalendarResults}
        isConfigured={submitCalendarConfigured}
        submitting={submittingCalendar}
        onConfirm={() => void handleSubmitCalendarConfirm()}
        onClose={() => setShowSubmitCalendarDialog(false)}
      />

      <CloseCalendarDialog
        open={showCloseCalendarDialog}
        closing={closingCalendar}
        onConfirm={() => void handleCloseCalendarConfirm()}
        onClose={() => setShowCloseCalendarDialog(false)}
      />

      {showBulkEditFlyout && (
        <BulkEditFlyout
          selectedCount={selected.size}
          selectedIds={Array.from(selected)}
          userId={userId}
          isBusinessApprover={isBusinessApprover}
          isBusinessReviewer={isBusinessReviewer}
          onSave={(req) => void handleBulkFieldsSave(req)}
          onClose={() => setShowBulkEditFlyout(false)}
          submitting={bulkUpdateFields.isPending || bulkDraftPushToSummit.isPending}
        />
      )}

      <BulkAddCommentDialog
        open={showBulkAddCommentDialog}
        selectedCount={selected.size}
        submitting={bulkAddComment.isPending}
        onCancel={() => setShowBulkAddCommentDialog(false)}
        onSave={(comment) => void handleBulkAddComment(comment)}
      />
    </main>
  );
}

interface CalendarOptionsProps {
  canSubmit: boolean;
  isFYClosed: boolean;
  /** Blazor: isEnabled = !isSubmitted (calendar not yet submitted) */
  isCalendarSubmitted: boolean;
  /** Blazor: IsCloseCalendarEnable = calendarStatus === "Submitted" */
  isCloseCalendarEnabled: boolean;
  onSubmitCalendar: () => void;
  onCloseCalendar: () => void;
}

function CalendarOptionsMenu({ canSubmit, isFYClosed, isCalendarSubmitted, isCloseCalendarEnabled, onSubmitCalendar, onCloseCalendar }: CalendarOptionsProps) {
  const [open, setOpen] = useState(false);
  const canSubmitCalendar = canSubmit && !isCalendarSubmitted && !isFYClosed;
  const canCloseCalendar = canSubmit && isCalendarSubmitted && isCloseCalendarEnabled;

  return (
    <div className="relative">
      <button type="button" aria-haspopup="true" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="submit-button inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium">
        Calendar Options &#x25BE;
      </button>
      {open && (
        <ul role="menu" className="bulkAction-custom-popup absolute right-0 top-full z-50 mt-1"
          onMouseLeave={() => setOpen(false)}>
          <li role="none">
            <button role="menuitem" type="button" disabled={!canSubmitCalendar}
              onClick={() => { setOpen(false); onSubmitCalendar(); }}
              className="drop-down-buttons w-full px-4 py-2 text-left text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Submit Calendar
            </button>
          </li>
          <li role="none">
            <button role="menuitem" type="button" disabled={!canCloseCalendar}
              onClick={() => { setOpen(false); void onCloseCalendar(); }}
              className="drop-down-buttons w-full px-4 py-2 text-left text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Close Calendar
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
